/**
 * SP Date Picker
 * ---------------------------------------------------------------------------
 * Replaces the browser's date picker on [data-sp-date-picker] inputs with a
 * calendar built from the theme's own tokens.
 *
 * THE NATIVE INPUT STAYS. It remains the single source of truth for the value,
 * so the form, its `required` validation, and the existing :invalid-driven
 * "Enter Date" placeholder all keep working untouched. This widget only writes
 * to it and dispatches the events a real edit would. Deleting the input and
 * holding the value in JS would have broken three things that currently need
 * no JS at all.
 *
 * WHY REPLACE THE NATIVE PICKER ON MOBILE TOO. The usual advice is to let
 * phones show their OS picker. That is right when the field is a generic date,
 * and wrong here: this field means "when are you thinking of joining", the
 * useful range is a few months around today, and the OS picker offers a
 * scroll-wheel over decades with no sense of that range. The custom sheet can
 * disable what is out of range and mark today, which is the actual job.
 *
 * The input keeps type="date" rather than becoming type="text": it preserves
 * the value format, validation and assistive-tech semantics. The browser's own
 * picker is suppressed by preventing the events that open it.
 */

(function () {
  'use strict';

  var DOW = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
                'August', 'September', 'October', 'November', 'December'];

  function startOfDay(d) {
    var c = new Date(d.getTime());
    c.setHours(0, 0, 0, 0);
    return c;
  }

  function iso(d) {
    // Local parts, not toISOString: toISOString converts to UTC and can hand
    // back the previous day for anyone west of Greenwich.
    var m = String(d.getMonth() + 1).padStart(2, '0');
    var day = String(d.getDate()).padStart(2, '0');
    return d.getFullYear() + '-' + m + '-' + day;
  }

  function parseISO(value) {
    var parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
    if (!parts) return null;
    return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  }

  function sameDay(a, b) {
    return a && b && a.getFullYear() === b.getFullYear() &&
           a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }

  function DatePicker(input) {
    this.input = input;
    this.today = startOfDay(new Date());
    this.min = input.min ? parseISO(input.min) : this.today;
    this.max = input.max ? parseISO(input.max) : null;
    this.open = false;
    this.build();
    this.bind();
  }

  DatePicker.prototype.build = function () {
    var root = document.createElement('div');
    root.className = 'sp-dp';
    root.hidden = true;
    // A dialog rather than a listbox: it holds its own controls and traps
    // focus, and screen readers announce the month via aria-label.
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'false');
    root.setAttribute('aria-label', 'Choose a date');

    root.innerHTML =
      '<div class="sp-dp__handle" aria-hidden="true"></div>' +
      '<div class="sp-dp__head">' +
        '<button type="button" class="sp-dp__nav" data-dp-prev aria-label="Previous month">' +
          '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M10 3.5L5.5 8L10 12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '</button>' +
        '<h2 class="sp-dp__title" data-dp-title aria-live="polite"></h2>' +
        '<button type="button" class="sp-dp__nav" data-dp-next aria-label="Next month">' +
          '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M6 3.5L10.5 8L6 12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '</button>' +
      '</div>' +
      '<div class="sp-dp__dows" aria-hidden="true">' +
        DOW.map(function (d) { return '<span class="sp-dp__dow">' + d + '</span>'; }).join('') +
      '</div>' +
      '<div class="sp-dp__grid" role="grid" data-dp-grid></div>' +
      '<div class="sp-dp__foot">' +
        '<button type="button" class="sp-dp__action" data-dp-today>Today</button>' +
        '<button type="button" class="sp-dp__action" data-dp-close>Done</button>' +
      '</div>';

    var scrim = document.createElement('div');
    scrim.className = 'sp-dp-scrim';
    scrim.hidden = true;

    document.body.appendChild(scrim);
    document.body.appendChild(root);

    this.root = root;
    this.scrim = scrim;
    this.grid = root.querySelector('[data-dp-grid]');
    this.title = root.querySelector('[data-dp-title]');
    this.prevBtn = root.querySelector('[data-dp-prev]');
    this.nextBtn = root.querySelector('[data-dp-next]');
  };

  DatePicker.prototype.selected = function () {
    return parseISO(this.input.value);
  };

  DatePicker.prototype.inRange = function (d) {
    if (this.min && d < this.min) return false;
    if (this.max && d > this.max) return false;
    return true;
  };

  DatePicker.prototype.render = function () {
    var view = this.view;
    var sel = this.selected();
    this.title.textContent = MONTHS[view.getMonth()] + ' ' + view.getFullYear();

    var first = new Date(view.getFullYear(), view.getMonth(), 1);
    var lead = first.getDay();
    var days = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();

    var html = '';
    for (var i = 0; i < lead; i++) {
      html += '<span class="sp-dp__day sp-dp__day--outside" aria-hidden="true"></span>';
    }
    for (var n = 1; n <= days; n++) {
      var d = new Date(view.getFullYear(), view.getMonth(), n);
      var cls = 'sp-dp__day';
      if (sameDay(d, this.today)) cls += ' sp-dp__day--today';
      if (sameDay(d, sel)) cls += ' sp-dp__day--selected';
      var ok = this.inRange(d);
      html += '<button type="button" class="' + cls + '" role="gridcell"' +
              ' data-dp-day="' + iso(d) + '"' +
              ' tabindex="' + (sameDay(d, this.focusDate) ? '0' : '-1') + '"' +
              (ok ? '' : ' disabled') +
              ' aria-selected="' + (sameDay(d, sel) ? 'true' : 'false') + '"' +
              ' aria-label="' + MONTHS[d.getMonth()] + ' ' + n + ', ' + d.getFullYear() + '">' +
              n + '</button>';
    }
    this.grid.innerHTML = html;

    // Disable a nav arrow only when the WHOLE adjacent month is out of range,
    // not when its first day is: a min of the 15th must still allow browsing
    // back to that month.
    var prevLast = new Date(view.getFullYear(), view.getMonth(), 0);
    this.prevBtn.disabled = Boolean(this.min && prevLast < this.min);
    var nextFirst = new Date(view.getFullYear(), view.getMonth() + 1, 1);
    this.nextBtn.disabled = Boolean(this.max && nextFirst > this.max);
  };

  DatePicker.prototype.position = function () {
    if (window.matchMedia('(min-width: 768px)').matches) {
      var r = this.input.getBoundingClientRect();
      var w = this.root.offsetWidth;
      var h = this.root.offsetHeight;
      var left = Math.min(Math.max(8, r.left), window.innerWidth - w - 8);
      // Flip above the field when there is not room below, so the calendar is
      // never clipped by the viewport on a field low on the page.
      var below = r.bottom + 8;
      var top = (below + h > window.innerHeight - 8 && r.top - h - 8 > 8)
        ? r.top - h - 8
        : below;
      this.root.style.left = left + 'px';
      this.root.style.top = top + 'px';
    } else {
      this.root.style.left = '';
      this.root.style.top = '';
    }
  };

  DatePicker.prototype.show = function () {
    if (this.open) return;
    this.open = true;
    var sel = this.selected();
    this.focusDate = sel && this.inRange(sel) ? sel : this.today;
    this.view = new Date(this.focusDate.getFullYear(), this.focusDate.getMonth(), 1);
    this.root.hidden = false;
    this.scrim.hidden = false;
    // Modal on the sheet tier (it has a scrim and owns the screen), not on the
    // popover tier (the page behind it stays usable). Set per open, because a
    // resize between opens can change which control this is.
    this.root.setAttribute('aria-modal',
      window.matchMedia('(min-width: 768px)').matches ? 'false' : 'true');
    document.documentElement.classList.add('sp-dp-open');
    this.render();
    this.position();
    this.focusCurrent();
  };

  DatePicker.prototype.hide = function (returnFocus) {
    if (!this.open) return;
    this.open = false;
    this.root.hidden = true;
    this.scrim.hidden = true;
    document.documentElement.classList.remove('sp-dp-open');
    if (returnFocus !== false) this.input.focus();
  };

  DatePicker.prototype.focusCurrent = function () {
    var cell = this.grid.querySelector('[data-dp-day="' + iso(this.focusDate) + '"]');
    if (cell) cell.focus();
  };

  DatePicker.prototype.moveFocus = function (days) {
    var next = new Date(this.focusDate.getTime());
    next.setDate(next.getDate() + days);
    if (this.min && next < this.min) return;
    if (this.max && next > this.max) return;
    this.focusDate = next;
    this.view = new Date(next.getFullYear(), next.getMonth(), 1);
    this.render();
    this.focusCurrent();
  };

  DatePicker.prototype.commit = function (value) {
    this.input.value = value;
    // Both events, because listeners in the wild bind to either, and a
    // programmatic value assignment fires neither on its own.
    this.input.dispatchEvent(new Event('input', { bubbles: true }));
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
    this.hide();
  };

  DatePicker.prototype.bind = function () {
    var self = this;

    // Suppress the native picker. showPicker() is what Chrome calls on click
    // for type=date, so the click has to be intercepted before default.
    ['click', 'mousedown'].forEach(function (type) {
      self.input.addEventListener(type, function (e) {
        e.preventDefault();
        self.show();
      });
    });

    self.input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        self.show();
      }
    });

    self.root.addEventListener('click', function (e) {
      var day = e.target.closest('[data-dp-day]');
      if (day && !day.disabled) { self.commit(day.getAttribute('data-dp-day')); return; }
      if (e.target.closest('[data-dp-prev]')) { self.moveMonth(-1); return; }
      if (e.target.closest('[data-dp-next]')) { self.moveMonth(1); return; }
      if (e.target.closest('[data-dp-today]')) {
        if (self.inRange(self.today)) self.commit(iso(self.today));
        return;
      }
      if (e.target.closest('[data-dp-close]')) { self.hide(); }
    });

    self.root.addEventListener('keydown', function (e) {
      var k = e.key;
      if (k === 'Escape') { e.preventDefault(); self.hide(); return; }
      if (!e.target.closest('[data-dp-day]')) return;
      var map = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
      if (k in map) { e.preventDefault(); self.moveFocus(map[k]); return; }
      if (k === 'PageUp') { e.preventDefault(); self.moveMonth(-1, true); return; }
      if (k === 'PageDown') { e.preventDefault(); self.moveMonth(1, true); return; }
      if (k === 'Home') { e.preventDefault(); self.moveFocus(-self.focusDate.getDay()); return; }
      if (k === 'End') { e.preventDefault(); self.moveFocus(6 - self.focusDate.getDay()); }
    });

    self.scrim.addEventListener('click', function () { self.hide(); });

    document.addEventListener('pointerdown', function (e) {
      if (!self.open) return;
      if (self.root.contains(e.target) || e.target === self.input) return;
      self.hide(false);
    });

    window.addEventListener('resize', function () { if (self.open) self.position(); });
    window.addEventListener('scroll', function () { if (self.open) self.position(); }, true);
  };

  DatePicker.prototype.moveMonth = function (delta, keepFocus) {
    this.view = new Date(this.view.getFullYear(), this.view.getMonth() + delta, 1);
    if (keepFocus) {
      var f = new Date(this.focusDate.getTime());
      f.setMonth(f.getMonth() + delta);
      this.focusDate = f;
    } else {
      // Keep the roving tabindex on a day that exists in the new month.
      var days = new Date(this.view.getFullYear(), this.view.getMonth() + 1, 0).getDate();
      this.focusDate = new Date(this.view.getFullYear(), this.view.getMonth(),
                                Math.min(this.focusDate.getDate(), days));
    }
    this.render();
    this.position();
    if (keepFocus) this.focusCurrent();
  };

  function init() {
    document.querySelectorAll('[data-sp-date-picker]').forEach(function (input) {
      if (input.dataset.spDpReady) return;
      input.dataset.spDpReady = '1';
      new DatePicker(input);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
